import * as React from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  PrimaryButton,
  makeStyles,
  createStyles,
  Theme,
  HideOffScreen,
  TableSortLabel,
} from '@bb-ui/react-library';
import { LoadingIndicator } from 'components/LoadingIndicator';
import LearnSiteRow from './LearnSiteRow';
import { LearnInstance } from '@bb-learn-instances-service/common';
import { useTranslation } from 'react-i18next';
import { DatabaseLookup } from './learnSiteFilters';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footerContainer: {
      height: 80,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
      position: 'relative',
    },
    spinnerOverlay: {
      position: 'absolute',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      inset: 0,
    },
    tableFixedLayout: {
      tableLayout: 'fixed',
      width: '100%',
    },
    rowHeight: {
      height: 40,
      // Rows grow with wrapped text; keep every cell's content at the top, as in Captain
      '& td': {
        verticalAlign: 'top',
      },
    },
    // Wrap long values onto more lines, as the Captain list does, instead of cutting them off.
    // URLs and RDS names have no spaces, so they may break anywhere.
    cellText: {
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    },
    lockHeader: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: 0,
    },
    noResultsContainer: {
      padding: theme.spacing(4),
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
  }),
);

const NO_DATABASES: DatabaseLookup = { sharedDatabases: {}, loaded: false };

export type LockSort = 'none' | 'lockedFirst' | 'lockedLast';

// Locked first reads as descending: locked (1) above unlocked (0)
const sortDirection = (lockSort: Exclude<LockSort, 'none'>) =>
  lockSort === 'lockedFirst' ? 'desc' : 'asc';

export interface LearnSitesTableProps {
  sites: LearnInstance[];
  nextPageToken?: string;
  loading: boolean;
  loadMore: () => void;
  isSearching: boolean;
  databases?: DatabaseLookup;
  /** Show the Database column; it is only filled while the Database filter is on */
  showDatabase?: boolean;
  /** Show the lock column and its sort; lock state is only read while the Locked filter is on */
  showLock?: boolean;
  lockSort?: LockSort;
  onToggleLockSort?: () => void;
  searchExhausted?: boolean;
  allDataFetched?: boolean;
}

export const LearnSitesTable: React.FunctionComponent<LearnSitesTableProps> = ({
  sites,
  nextPageToken,
  loading,
  loadMore,
  isSearching,
  databases = NO_DATABASES,
  showDatabase = false,
  showLock = false,
  lockSort = 'none',
  onToggleLockSort,
  searchExhausted = false,
  allDataFetched = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const renderFooterContent = () => {
    // If searching and no results found after exhausting all pages
    if (isSearching && searchExhausted && sites.length === 0) {
      return (
        <Typography data-testid="footer-search-no-results">
          {t('learnSites.searchNoResults')}
        </Typography>
      );
    }

    // If searching and loading more pages
    if (isSearching && loading && nextPageToken && !allDataFetched) {
      return (
        <div>
          <LoadingIndicator data-testid="footer-search-spinner" />
          <Typography variant="caption" style={{ marginTop: 8 }}>
            {t('learnSites.searchingMorePages')}
          </Typography>
        </div>
      );
    }

    // If searching and found results, show completion message
    if (isSearching && !loading && sites.length > 0) {
      const message = allDataFetched
        ? t('learnSites.searchComplete', { count: sites.length })
        : t('learnSites.searchPartialResults', { count: sites.length });

      return <Typography data-testid="footer-search-complete">{message}</Typography>;
    }

    // Normal pagination mode (not searching)
    if (!isSearching) {
      if (loading && nextPageToken && !allDataFetched) {
        return <LoadingIndicator data-testid="footer-spinner" />;
      }

      if (!loading && nextPageToken && !allDataFetched) {
        return (
          <PrimaryButton onClick={loadMore} data-testid="footer-load-button">
            {t('global.loadMore')}
          </PrimaryButton>
        );
      }

      if (allDataFetched && sites.length > 0) {
        return <Typography data-testid="footer-no-more">{t('learnSites.noMoreData')}</Typography>;
      }
    }

    return null;
  };

  // Show empty state if no sites
  if (sites.length === 0 && !loading && !isSearching) {
    return (
      <div className={classes.noResultsContainer}>
        <Typography variant="h6" gutterBottom>
          {t('learnSites.noSitesTitle')}
        </Typography>
        <Typography>{t('learnSites.noSitesMessage')}</Typography>
      </div>
    );
  }

  return (
    <TableContainer>
      <Table className={classes.tableFixedLayout}>
        <colgroup>
          {showLock && <col style={{ width: '3%' }} />}
          <col style={{ width: '14%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '10%' }} />
          {showDatabase && <col style={{ width: '12%' }} />}
          <col style={{ width: '11%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '6%' }} />
        </colgroup>
        <TableHead>
          <TableRow className={classes.rowHeight}>
            {showLock && (
              <TableCell
                className={classes.lockHeader}
                sortDirection={lockSort === 'none' ? false : sortDirection(lockSort)}
              >
                <TableSortLabel
                  active={lockSort !== 'none'}
                  direction={lockSort === 'none' ? 'desc' : sortDirection(lockSort)}
                  onClick={onToggleLockSort}
                  aria-label={t('learnSites.sortByLock')}
                  data-testid="lock-sort"
                >
                  <HideOffScreen>{t('learnSites.columns.locked')}</HideOffScreen>
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell className={classes.cellText}>{t('learnSites.columns.name')}</TableCell>
            <TableCell className={classes.cellText}>{t('learnSites.columns.clientName')}</TableCell>
            <TableCell className={classes.cellText}>
              {t('learnSites.columns.installationId')}
            </TableCell>
            <TableCell className={classes.cellText}>{t('learnSites.columns.status')}</TableCell>
            <TableCell className={classes.cellText}>{t('learnSites.columns.fleetId')}</TableCell>
            {showDatabase && (
              <TableCell className={classes.cellText}>{t('learnSites.columns.database')}</TableCell>
            )}
            <TableCell className={classes.cellText}>{t('learnSites.columns.createdAt')}</TableCell>
            <TableCell className={classes.cellText}>{t('learnSites.columns.url')}</TableCell>
            <TableCell>
              <HideOffScreen>{t('learnSites.columns.actions')}</HideOffScreen>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sites.map((site, index) => (
            <LearnSiteRow
              key={`${site.TenantId}-${site.InstallationId}`}
              site={site}
              index={index}
              rowClass={classes.rowHeight}
              cellTextClass={classes.cellText}
              databases={databases}
              showDatabase={showDatabase}
              showLock={showLock}
            />
          ))}
        </TableBody>
      </Table>

      <div className={classes.footerContainer}>{renderFooterContent()}</div>
    </TableContainer>
  );
};

export default LearnSitesTable;
